<template>
    <section class="px-4 py-14 mx-auto max-w-screen-2xl md:pt-28 mb:pb-16 lg:px-6 sm:px-7 md-max:pb-10 md-max:pt-10">
        <div class="mx-auto md:pb-6">
            <h2
                class="text-3xl font-bold text-center text-black lg:leading-10 xl:text-42 md:text-4xl font-ww-bold"
                v-html="headingText">
            </h2>

            <div class="flex flex-col gap-y-2 gap-x-8 justify-center items-center py-8 md:mt-6 md:flex-row sm:gap-y-8">
                <div class="flex gap-y-4 gap-x-6 justify-center items-center md:mr-8">
                    <div class="text-6xl xl:text-7xl font-ww-regular text-lifeboost-orange-dark">
                        {{ ratings?.avg_rating }}
                    </div>
                    <div class="flex flex-col justify-between items-start h-full">
                        <div class="md:mb-2 stars-small-rating">
                            <SectionMushroomNewBuilderRaitingStars :rating="5" />
                        </div>
                        <div class="text-base leading-7 xl:text-lg font-ww-regular">
                            Based on {{ ratings?.reviews_count }} reviews
                        </div>
                    </div>
                </div>
                <div class="flex relative py-2.5 h-full">
                    <div class="absolute w-0.5 md:-left-8 md:block hidden h-full bottom-0 bg-[#cdcdcd]"></div>
                    <a href="https://lifeboostcoffee.com/products/cognition-mushroom-coffee#judgeme_product_reviews" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="px-10 py-3.5 text-lg text-white rounded-full bg-lifeboost-orange-dark font-ww-medium"
                        aria-label="Write a review for Lifeboost Cognition Coffee"
                        title="Write a review for Lifeboost Cognition Coffee">
                        Write a review
                    </a>
                </div>
            </div>
        </div>

        <!-- Reviews List -->
        <div class="mx-auto" ref="reviewsSection">
            <div v-for="(review, index) in reviews" :key="review.id" role="article" tabindex="0"
                :aria-labelledby="'review-' + review.id"
                class="overflow-hidden py-6 w-full border-b-2 md:py-12 bg-lifeboost-blue-lighter border-gray"
                :class="{ 'border-none': index === reviews.length - 1 }">
                <!-- Review Content -->
                <div class="grid grid-cols-12 gap-y-4 gap-x-6 md:gap-y-0">
                    <!-- Reviewer Info -->
                    <div class="flex col-span-12 gap-x-4 items-center lg:col-span-4 xl:col-span-3">
                        <div>
                            <div
                                class="flex justify-center items-center w-16 h-16 text-white rounded-full md:w-24 md:h-24 bg-lifeboost-gray">
                                <div class="flex justify-center items-center text-4xl leading-none md:text-53">
                                    {{ review.reviewer.name[0] }}
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col gap-y-3 w-full md:w-auto">
                            <div role="heading" aria-level="2" :id="'review-' + review.id"
                                class="flex flex-wrap gap-x-4 justify-between items-center font-heading sm:flex-row">
                                <div
                                    class="text-lg font-bold xl:text-2xl md:text-xl text-lifeboost-gray-lighter font-ww-medium">
                                    {{ review.reviewer.name }}
                                </div>
                                <div class="text-base text-lifeboost-gray-lighter md:hidden xl:text-xl md:text-lg">
                                    {{ formatDate(review.created_at) }}
                                </div>
                            </div>
                            <div>
                                <span
      class="inline-flex items-center px-2 py-1.5 font-normal rounded-full font-ww-regular bg-lifeboost-green-dark text-lifeboost-green-dark-green">
     
      <span class="mx-2 text-xs text-white md:text-sm">Verified Buyer</span>
      <!-- <span class="sr-only">{{ component.srText }}</span> -->
    </span>
                            </div>
                        </div>
                    </div>

                    <!-- Review Details -->
                    <div class="grid grid-cols-12 col-span-12 lg:col-span-8 xl:col-span-9 md:gap-x-8 md:mt-0">
                        <div class="flex flex-col col-span-12 justify-center lg:pr-10">
                            <div class="flex justify-between items-center md-max:w-full">
                                <div class="flex gap-x-3 items-center md-max:w-full md-max:inline-block">
                                    <div class="float-left md-max:pr-2 stars-small-rating">
                                        <SectionMushroomNewBuilderRaitingStars :rating="review.rating" />
                                    </div>
                                    <div class="text-lg font-bold text-lifeboost-gray-lighter font-ww-medium xl:text-2xl md:text-xl"
                                        v-if="review.title">
                                        {{ review.title }}
                                    </div>
                                </div>
                                <div class="hidden text-base text-lifeboost-gray-lighter md:block font-ww-regular xl:text-xl md:text-lg">
                                    {{ formatDate(review.created_at) }}
                                </div>
                            </div>
                            <div class="pt-2 md:pt-4">
                                <div class="flex flex-wrap lg:w-10/12">
                                    <div class="w-full">
                                        <div
                                            class="max-w-2xl text-base md:text-lg xl:text-xl text-lifeboost-gray-lighter font-ww-regular">
                                            {{ review.body }}
                                        </div>
                                        <!-- <div v-if="review.pictures && review.pictures.some((el) => !el.hidden)"
                                            class="flex flex-row mb-8 max-w-2xl leading-loose">
                                            <ElementImageReviews :center="false" :pictures="review.pictures"
                                                @on-show="(index, array) => $emit('onShow', index, array)" />
                                        </div> -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- End of Review Content -->
            </div>

            <div class="flex justify-center mt-6 space-x-4 md:mb-10">
                <!-- Previous Button -->
                <button aria-label="Previous review" @click="changePage(currentPage - 1)" v-if="currentPage > 1 && !loading" class="pr-2">
                    <svg class="rotate-180" width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.78516 2.23047L9.05452 9.49983L1.78516 16.7692" stroke="#4B3D2C" stroke-width="2.56566" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                  
                </button>

                <!-- Page Numbers -->
                <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="changePage(page)"
                    class="text-xl md:text-22 font-ww-regular"
                    :class="['cursor-pointer text-lifeboost-gray-lighter', { 'font-bold md:!text-3xl !text-2xl font-ww-bold mb-1 md:pt-0': currentPage === page, 'text-gray-400 !font-medium': currentPage !== page }]"
                >
                    {{ page }}
                </button>

                <!-- Next Button -->
                <button aria-label="Next review" @click="changePage(currentPage + 1)" v-if="currentPage < totalPages && !loading" class="pl-2 text-27">
                    <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.78516 2.23047L9.05452 9.49983L1.78516 16.7692" stroke="#4B3D2C" stroke-width="2.56566" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center mt-4">
                <span>Loading reviews...</span>
            </div>

            <!-- Error State -->
            <div v-if="error" class="flex justify-center mt-4 text-red-500">
                <span>{{ error }}</span>
            </div>
        </div>
    </section>
</template>

<script setup>

import { ref, computed, onMounted } from 'vue';

const props = defineProps({
    headingText: {
        type: String,
        default: "The 5-star reviews are pouring in!"
    },
    productId: {
        type: String,
        default: "7387365703740"
    }
});

const reviews = ref([]);
const currentPage = ref(1);
const reviewsPerPage = ref(6);
const totalPages = ref(1);
const loading = ref(false);
const error = ref(null);
const hasMoreReviews = ref(true);
const ratings = ref();

const reviewsSection = ref(null);

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0'); 
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

const fetchReviews = async (productId, page = 1) => {
    loading.value = true;
    error.value = null;

    try {
        const response = await fetch(
            `/api/fetchReviews?product_id=${productId}&page=${page}&limit=${reviewsPerPage.value}`
        );

        fetchRaitingReview(productId, 'cognition-mushroom-coffee');

        if (!response.ok) {
            throw new Error('Failed to fetch reviews');
        }

        const data = await response.json();
        if (Array.isArray(data.reviews)) {
            reviews.value = data.reviews;
            totalPages.value = Math.ceil((ratings.value?.reviews_count || 0) / reviewsPerPage.value);
            hasMoreReviews.value = data.reviews.length === reviewsPerPage.value;
        } else {
            reviews.value = [];
            hasMoreReviews.value = false;
        }
    } catch (err) {
        error.value = err.message || 'An error occurred while fetching reviews.';
    } finally {
        loading.value = false;
    }
};

const fetchRaitingReview = async (productId, handle) => {
    try {
        const response = await fetch(
            `/api/fetchReviewsCount?product_id=${productId}&handle=${handle}`
        );

        if (!response.ok) {
            throw new Error('Failed to fetch reviews');
        }

        const data = await response.json();
        ratings.value = data;
    } catch (err) {
        error.value = err.message || 'An error occurred while fetching reviews.';
    } finally {
        loading.value = false;
    }
};

const changePage = (page) => {
    if (page < 1 || page > totalPages.value) return;
    currentPage.value = page;
    fetchReviews(props.productId, currentPage.value);

    if (reviewsSection.value) {
        reviewsSection.value.scrollIntoView({ behavior: 'smooth' });
    }
};

const visiblePages = computed(() => {
    let start = Math.max(1, currentPage.value - 1);
    let end = Math.min(totalPages.value, currentPage.value + 2);

    if (end - start < 3 && start > 1) {
        start = Math.max(1, end - 3);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

onMounted(() => {
    fetchRaitingReview(props.productId, 'cognition-mushroom-coffee').then(() => {
        fetchReviews(props.productId, currentPage.value);
    });
});

defineBuilderComponent({
    friendlyName: "Review With Comments And ProductId",
    inputs: [
        {
            name: "headingText",
            type: "richText",
            friendlyName: "Heading Text",
            defaultValue: "The 5-star reviews are pouring in!",
        },
        {
            name: "productId",
            type: "string",
            friendlyName: "Product ID",
            defaultValue: "7387365703740",
        },
    ]
});
</script>